<template>
  <section class="page-section">
    <div class="toolbar">
      <div class="toolbar-left">
        <el-input v-model="filters.search" placeholder="按用品名称搜索" clearable class="toolbar-input" />
        <el-button type="primary" @click="handleSearch">搜索</el-button>
        <el-button @click="handleReset">重置</el-button>
      </div>
      <el-button type="primary" @click="openSupplyDialog()">新增办公用品</el-button>
    </div>

    <el-table :data="supplies.content" class="business-table">
      <el-table-column prop="name" label="用品名称" min-width="160" />
      <el-table-column prop="spec" label="规格" min-width="140" />
      <el-table-column prop="stock" label="库存" width="90" />
      <el-table-column prop="price" label="单价" width="100">
        <template #default="{ row }">¥ {{ row.price }}</template>
      </el-table-column>
      <el-table-column prop="location" label="存放位置" min-width="140" />
      <el-table-column prop="warningNum" label="预警阈值" width="110" />
      <el-table-column label="状态" width="120">
        <template #default="{ row }">
          <el-tag :type="row.warning ? 'danger' : 'success'">
            {{ row.warning ? '库存紧张' : '库存健康' }}
          </el-tag>
        </template>
      </el-table-column>
      <el-table-column label="操作" width="220" fixed="right">
        <template #default="{ row }">
          <el-button link type="primary" @click="openSupplyDialog(row)">编辑</el-button>
          <el-button link type="danger" @click="handleDeleteSupply(row)">删除</el-button>
        </template>
      </el-table-column>
    </el-table>

    <div v-if="supplies.totalElements > 0" class="pager">
      <span class="pager-text">第 {{ filters.page }} / {{ totalPages }} 页</span>
      <el-button :disabled="filters.page <= 1" @click="handlePrevPage">
        <el-icon><ArrowLeft /></el-icon>
        上一页
      </el-button>
      <el-button type="primary" :disabled="filters.page >= totalPages" @click="handleNextPage">
        下一页
        <el-icon><ArrowRight /></el-icon>
      </el-button>
    </div>

    <el-dialog v-model="dialogVisible" :title="supplyForm.id ? '编辑用品' : '新增用品'" width="640px">
      <el-form ref="supplyFormRef" :model="supplyForm" :rules="supplyRules" label-position="top" class="supply-form">
        <div class="form-tip">
          <span>基础信息</span>
          <p>请完整填写用品资料，库存预警阈值将直接参与低库存提醒。</p>
        </div>

        <div class="form-grid">
          <el-form-item label="用品名称" prop="name">
            <el-input v-model="supplyForm.name" placeholder="例如：A4打印纸" />
          </el-form-item>
          <el-form-item label="规格" prop="spec">
            <el-input v-model="supplyForm.spec" placeholder="例如：70g/500张" />
          </el-form-item>
          <el-form-item label="库存数量" prop="stock">
            <el-input-number v-model="supplyForm.stock" :min="0" class="full-width" />
          </el-form-item>
          <el-form-item label="单价" prop="price">
            <el-input-number v-model="supplyForm.price" :min="0" :step="0.1" class="full-width" />
          </el-form-item>
          <el-form-item label="存放位置" prop="location">
            <el-input v-model="supplyForm.location" placeholder="例如：A区库房-03" />
          </el-form-item>
          <el-form-item label="预警阈值" prop="warningNum">
            <el-input-number v-model="supplyForm.warningNum" :min="0" class="full-width" />
          </el-form-item>
        </div>
      </el-form>
      <template #footer>
        <el-button @click="dialogVisible = false">取消</el-button>
        <el-button type="primary" @click="submitSupply">{{ supplyForm.id ? '更新数据' : '新增用品' }}</el-button>
      </template>
    </el-dialog>
  </section>
</template>

<script>
import { ElMessage, ElMessageBox } from 'element-plus'
import { ArrowLeft, ArrowRight } from '@element-plus/icons-vue'
import {
  deleteAdminSupply,
  fetchAdminSupplies,
  saveAdminSupply,
  updateAdminSupply
} from '../../services/adminService'

function emptySupply() {
  return {
    id: null,
    name: '',
    spec: '',
    stock: 0,
    price: 0,
    location: '',
    warningNum: 0
  }
}

export default {
  name: 'AdminSuppliesPanel',
  components: {
    ArrowLeft,
    ArrowRight
  },
  data() {
    return {
      supplies: {
        content: [],
        totalElements: 0
      },
      filters: {
        search: '',
        page: 1,
        pageSize: 10
      },
      dialogVisible: false,
      supplyForm: emptySupply(),
      supplyRules: {
        name: [{ required: true, message: '请输入用品名称', trigger: 'blur' }],
        spec: [{ required: true, message: '请输入规格', trigger: 'blur' }],
        stock: [{ required: true, message: '请输入库存数量', trigger: 'change' }],
        price: [{ required: true, message: '请输入单价', trigger: 'change' }],
        location: [{ required: true, message: '请输入存放位置', trigger: 'blur' }],
        warningNum: [{ required: true, message: '请输入预警阈值', trigger: 'change' }]
      }
    }
  },
  computed: {
    totalPages() {
      const total = Number(this.supplies.totalElements) || 0
      const pageSize = Number(this.filters.pageSize) || 10

      return Math.max(1, Math.ceil(total / pageSize))
    }
  },
  mounted() {
    this.loadSupplies()
    console.count('AdminSuppliesPanel mounted')
  },
  methods: {
    isCancelAction(error) {
      return error === 'cancel' || error === 'close'
    },
    async loadSupplies() {
      const response = await fetchAdminSupplies(this.filters)
      this.supplies = response.data
      console.count('loadSupplies')
    },
    handlePageChange(page) {
      this.filters.page = page
      this.loadSupplies()
    },
    handlePrevPage() {
      if (this.filters.page <= 1) {
        return
      }

      this.handlePageChange(this.filters.page - 1)
    },
    handleNextPage() {
      if (this.filters.page >= this.totalPages) {
        return
      }

      this.handlePageChange(this.filters.page + 1)
    },
    handleSearch() {
      this.filters.page = 1
      this.loadSupplies()
    },
    handleReset() {
      this.filters.search = ''
      this.filters.page = 1
      this.loadSupplies()
    },
    openSupplyDialog(row) {
      this.supplyForm = row ? { ...row } : emptySupply()
      this.dialogVisible = true
    },
    async submitSupply() {
      const valid = await this.$refs.supplyFormRef.validate().catch(() => false)

      if (!valid) {
        return
      }

      try {
        const isEdit = Boolean(this.supplyForm.id)
        const response = isEdit
          ? await updateAdminSupply(this.supplyForm)
          : await saveAdminSupply(this.supplyForm)

        ElMessage.success(response.message || response.msg || (isEdit ? '更新成功' : '新增成功'))
        this.dialogVisible = false
        this.loadSupplies()
      } catch (error) {
        ElMessage.error(error.message)
      }
    },
    async handleDeleteSupply(row) {
      try {
        await ElMessageBox.confirm(`确认删除用品「${row.name}」吗？`, '删除确认', {
          type: 'warning'
        })
        const response = await deleteAdminSupply(row.id)
        ElMessage.success(response.message || response.msg || '删除成功')
        this.loadSupplies()
      } catch (error) {
        if (!this.isCancelAction(error)) {
          ElMessage.error(error.message || '操作已取消')
        }
      }
    }
  }
}
</script>

<style scoped>
.page-section {
  display: grid;
  gap: 22px;
}

.toolbar {
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  gap: 12px;
}

.toolbar-left {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
}

.toolbar-input {
  width: 260px;
}

.business-table {
  border-radius: 22px;
  overflow: hidden;
}

.pager {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  flex-wrap: wrap;
}

.pager-text {
  color: #475569;
  font-size: 14px;
  font-weight: 600;
}

.supply-form {
  display: grid;
  gap: 18px;
}

.form-tip {
  padding: 14px 16px;
  border-radius: 16px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
}

.form-tip span {
  display: block;
  font-size: 14px;
  font-weight: 700;
  color: #0f172a;
}

.form-tip p {
  margin: 8px 0 0;
  color: #64748b;
  font-size: 13px;
  line-height: 1.6;
}

.form-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0 16px;
}

.full-width {
  width: 100%;
}

@media (max-width: 768px) {
  .toolbar-left,
  .toolbar-input {
    width: 100%;
  }

  .form-grid {
    grid-template-columns: 1fr;
  }
}
</style>
